import React, { useState, useCallback } from 'react';
import { useTranslation } from 'localization';
import type { ApiClients } from '../../../context/ApiClientsContext';
import type { Sphere } from 'types/global';
import {
  useDeployedApproaches,
  DeployedApproachInfo,
} from '../../../hooks/useDeployedApproaches';
import './SphereApproachesTab.scss';

export interface SphereApproachesTabProps {
  sphere: Sphere;
  organizationId: string;
  apiClients: ApiClients | null;
  hasEditPermission: boolean;
}

function getApproachDisplayName(
  approachName: Record<string, string> | undefined | null,
  fallback: string
): string {
  if (!approachName) return fallback;
  return approachName['en-US'] || approachName['en'] || Object.values(approachName)[0] || fallback;
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'deployed':
      return 'sphere-approaches-tab__badge--status-deployed';
    case 'deploying':
      return 'sphere-approaches-tab__badge--status-deploying';
    case 'failed':
      return 'sphere-approaches-tab__badge--status-failed';
    case 'paused':
      return 'sphere-approaches-tab__badge--status-paused';
    default:
      return '';
  }
}

const SphereApproachesTab: React.FC<SphereApproachesTabProps> = ({
  sphere,
  apiClients,
  hasEditPermission,
}) => {
  const { t } = useTranslation();

  const { directDeployments, inheritedDeployments, loading, error, refetch } =
    useDeployedApproaches(apiClients, sphere.id);

  const [isMutating, setIsMutating] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<DeployedApproachInfo | null>(null);

  const handleRemove = useCallback(
    async (deployment: DeployedApproachInfo) => {
      if (!apiClients?.graphClient) return;

      setIsMutating(true);
      setMutationError(null);
      setConfirmTarget(null);

      try {
        await apiClients.graphClient.post(
          `/approaches/deployments/${encodeURIComponent(deployment.deploymentId)}/remove`,
          {
            removeChildren: false,
            removeFactors: false,
            removeFlow: false,
            removeOrbit: false,
          }
        );
        refetch();
      } catch {
        setMutationError(t('sphereApproaches.removeError'));
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, refetch, t]
  );

  if (loading) {
    return (
      <div className="sphere-approaches-tab">
        <div className="sphere-approaches-tab__loading">
          <i className="pi pi-spin pi-spinner" />
        </div>
      </div>
    );
  }

  if (error && directDeployments.length === 0 && inheritedDeployments.length === 0) {
    return (
      <div className="sphere-approaches-tab">
        <div className="sphere-approaches-tab__error">{t('sphereApproaches.error')}</div>
      </div>
    );
  }

  const hasAnyDeployments = directDeployments.length > 0 || inheritedDeployments.length > 0;

  return (
    <div className="sphere-approaches-tab">
      {(mutationError || error) && (
        <div className="sphere-approaches-tab__error">
          {mutationError || t('sphereApproaches.error')}
        </div>
      )}

      {/* Direct Deployments */}
      <div className="sphere-approaches-tab__section">
        <h4 className="sphere-approaches-tab__section-title">
          {t('sphereApproaches.sections.direct')}
        </h4>
        {directDeployments.length === 0 ? (
          <p className="sphere-approaches-tab__empty">
            {t('sphereApproaches.empty')}
          </p>
        ) : (
          <div className="sphere-approaches-tab__list">
            {directDeployments.map((dep) => (
              <div key={dep.deploymentId} className="sphere-approaches-tab__card">
                <div className="sphere-approaches-tab__card-info">
                  <div className="sphere-approaches-tab__card-header">
                    <i className="pi pi-box" />
                    <span className="sphere-approaches-tab__card-name">
                      {getApproachDisplayName(dep.approachName, dep.approachSystemName)}
                    </span>
                    <span className="sphere-approaches-tab__badge sphere-approaches-tab__badge--version">
                      {t('sphereApproaches.card.version', { version: dep.approachVersion })}
                    </span>
                    <span className={`sphere-approaches-tab__badge ${getStatusClass(dep.deploymentStatus)}`}>
                      {t(`sphereApproaches.status.${dep.deploymentStatus}`)}
                    </span>
                  </div>
                  {dep.deployedAt && (
                    <p className="sphere-approaches-tab__card-meta">
                      {t('sphereApproaches.card.deployedAt', {
                        time: new Date(dep.deployedAt).toLocaleDateString(),
                        user: '',
                      })}
                    </p>
                  )}
                </div>
                {hasEditPermission && (
                  <div className="sphere-approaches-tab__card-actions">
                    <button
                      type="button"
                      className="sphere-approaches-tab__remove-btn"
                      onClick={() => setConfirmTarget(dep)}
                      disabled={isMutating}
                    >
                      <i className="pi pi-times" />
                      <span>{t('sphereApproaches.actions.remove')}</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inherited Deployments — hidden when empty */}
      {inheritedDeployments.length > 0 && (
        <div className="sphere-approaches-tab__section">
          <h4 className="sphere-approaches-tab__section-title">
            {t('sphereApproaches.sections.inherited')}
          </h4>
          <div className="sphere-approaches-tab__list">
            {inheritedDeployments.map((dep) => (
              <div key={dep.deploymentId} className="sphere-approaches-tab__card">
                <div className="sphere-approaches-tab__card-info">
                  <div className="sphere-approaches-tab__card-header">
                    <i className="pi pi-box" />
                    <span className="sphere-approaches-tab__card-name">
                      {getApproachDisplayName(dep.approachName, dep.approachSystemName)}
                    </span>
                    <span className="sphere-approaches-tab__badge sphere-approaches-tab__badge--version">
                      {t('sphereApproaches.card.version', { version: dep.approachVersion })}
                    </span>
                    <span className={`sphere-approaches-tab__badge ${getStatusClass(dep.deploymentStatus)}`}>
                      {t(`sphereApproaches.status.${dep.deploymentStatus}`)}
                    </span>
                    <span className="sphere-approaches-tab__badge sphere-approaches-tab__badge--inherited">
                      {t('sphereApproaches.card.inheritedFrom', { sphereName: dep.sphereId })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no deployments at all */}
      {!hasAnyDeployments && !error && (
        <p className="sphere-approaches-tab__empty">
          {t('sphereApproaches.empty')}
        </p>
      )}

      {/* Confirmation Dialog */}
      {confirmTarget && (
        <div
          className="marketplace-dialog-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmTarget(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setConfirmTarget(null);
          }}
        >
          <div
            className="marketplace-dialog sphere-approaches-tab__confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sphere-approaches-confirm-title"
          >
            <div className="marketplace-dialog__header">
              <h2 id="sphere-approaches-confirm-title" className="marketplace-dialog__title">
                {t('sphereApproaches.confirm.title')}
              </h2>
              <button
                type="button"
                className="marketplace-dialog__close-btn"
                onClick={() => setConfirmTarget(null)}
                aria-label={t('sphereApproaches.confirm.cancel')}
              >
                &times;
              </button>
            </div>
            <div className="marketplace-dialog__body">
              <p>
                {t('sphereApproaches.confirm.message', {
                  approachName: getApproachDisplayName(
                    confirmTarget.approachName,
                    confirmTarget.approachSystemName
                  ),
                })}
              </p>
            </div>
            <div className="marketplace-dialog__actions">
              <button
                type="button"
                className="marketplace-dialog__cancel-btn"
                onClick={() => setConfirmTarget(null)}
                disabled={isMutating}
              >
                {t('sphereApproaches.confirm.cancel')}
              </button>
              <button
                type="button"
                className="marketplace-dialog__submit-btn"
                onClick={() => handleRemove(confirmTarget)}
                disabled={isMutating}
              >
                {t('sphereApproaches.confirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SphereApproachesTab;
